import { type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { type SubscriptionTerm } from '@cx-sdk/subs';
import { isValidSubscriptionTerm } from '@cx-sdk/subs/schema';
import { type SubsProduct } from '@cx-web/domain-types';
import { useMyOCUser } from '@cx-web/feature-myoc';
import { useAuthModal, VIEW_LOGIN } from '@cx-web/feature-myoc-login/provider';
import { useRepubContext } from '@cx-web/feature-repub';
import { type PlusRepubConfig as RepubConfig, REPUB_CONFIG } from '@cx-web/repub-config';
import { getDefaultProduct } from '@cx-web/utils/getDefaultProduct';
import { Button } from '@fpsm/cxy-components/Button';
import { Card } from '@fpsm/cxy-components/Card';
import { List } from '@fpsm/cxy-components/List';
import clsx from 'clsx';

import { REPUBS } from '@cx-modules/constants';

import { URL_PARAMS } from '~/constants/url-params.ts';
import { navigateToStripeManagement } from '~/features/subscriptions/components/Subscribe/helpers/navigateToStripeManagement.ts';
import SubscriptionCard from '~/features/subscriptions/components/Subscribe/SubscriptionCard';
import { useSubscriptionFlow } from '~/features/subscriptions/hooks/use-subscription-flow.ts';
import { useTrackCTAImpression } from '~/hooks/use-track-cta-impression.ts';
import { IframeMessageType } from '~/pages/gz/types';
import { trackComponentWallCtaClick } from '~/utils/tracking';

import styles from './ComponentWall.module.scss';
import { PadlockIcon } from './PadlockIcon.tsx';

export type ComponentWallProps = {
  availableProducts: SubsProduct[];
  currentPath: string;
  redirectPath: string | undefined;
  subscriptionStatus: {
    hasEssentialSubscription: boolean;
    hasPremiumSubscription: boolean;
  };
  userHasCancelledSubscription: boolean;
  walledFeature: string;
};

export function ComponentWall({
  availableProducts,
  currentPath,
  redirectPath,
  subscriptionStatus,
  userHasCancelledSubscription,
  walledFeature,
  ...props
}: ComponentWallProps & PropsWithChildren) {
  const { repub, repubIs, repubPath } = useRepubContext();
  const { openAtView } = useAuthModal();
  const { t } = useTranslation();
  const { myocData } = useMyOCUser();
  const isLoggedIn = !!myocData?.myOCUser;

  // Get URL parameters
  const [searchParams] = useSearchParams();
  const cancelPayment = searchParams.get(URL_PARAMS.CANCELLED_PAYMENT);
  const isUpgrade = searchParams.get(URL_PARAMS.IS_UPGRADE);
  const price = searchParams.get(URL_PARAMS.PRICE);
  const termParam = searchParams.get(URL_PARAMS.TERM);
  const term: SubscriptionTerm | undefined = termParam && isValidSubscriptionTerm(termParam) ? termParam : undefined;
  const verifyPayment = searchParams.get(URL_PARAMS.VERIFY_PAYMENT);
  const isAttemptSubscribeOnPageLoad = searchParams.get(URL_PARAMS.IS_ATTEMPT_SUBSCRIBE);

  const config = (REPUB_CONFIG[repub]?.plus || REPUB_CONFIG[REPUBS.US.name]?.plus) as RepubConfig;

  // Find the default product based on isDefaultWhenSingleOption config
  const defaultSubscriptionProduct = getDefaultProduct(availableProducts);

  const isPaywallImmune = subscriptionStatus.hasPremiumSubscription;

  const isUserOnEssential = subscriptionStatus.hasEssentialSubscription;

  const canUpgrade = !config.paywall.isSinglePriceTier && isUserOnEssential;

  const shouldRedirectToPaymentPortal =
    isAttemptSubscribeOnPageLoad === 'true' && !isPaywallImmune && !(cancelPayment === 'true');

  const refForViewableImpressionTracking = useTrackCTAImpression({
    wallType: isUpgrade ? 'tools_upgrade_cta' : 'tools_paywall_cta',
    title: isUpgrade ? 'upgrade_component' : 'paywall_component',
  });

  const handleLoginClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (repubIs.GZ) return window.parentIFrame?.sendMessage({ type: IframeMessageType.OPEN_AUTH_MODAL });
    openAtView(VIEW_LOGIN);
  };

  // Use shared subscription flow hook
  const { handleProductClick } = useSubscriptionFlow(
    {
      cancelPayment,
      canUpgrade,
      isLoggedIn,
      isSubscribed: isPaywallImmune,
      productPrice: price,
      redirectPath,
      repub,
      repubPath,
      subscriptionTerm: term ?? undefined,
      userId: myocData?.myOCUser?.userID,
      userHasCancelledSubscription,
      verifyPayment,
    },
    {
      availableProducts,
      onBeforeSubscribe: product => {
        // ComponentWall-specific tracking
        trackComponentWallCtaClick({
          feature: walledFeature,
          isUpgrade: isUserOnEssential,
          price: product.priceFormatted,
          selection: config.paywall.ctaClickTrackingProductName,
          term: product.term,
        });

        // Handle already subscribed (paywall immune)
        if (isPaywallImmune) {
          return false;
        }

        // Handle upgrade flow for essential subscribers
        if (subscriptionStatus.hasEssentialSubscription) {
          navigateToStripeManagement({
            isUpgrade: isUserOnEssential,
            repub,
            upgradeProduct: product,
          });
          return false;
        }

        return true; // Continue normal flow
      },
      onPostLoginSubscribe: () => {
        // Return product to subscribe if conditions are met
        if (shouldRedirectToPaymentPortal) {
          return defaultSubscriptionProduct;
        }
        return undefined;
      },
    },
  );

  const handleSubscribe = () => {
    if (!defaultSubscriptionProduct) return;

    handleProductClick(
      {
        productId: defaultSubscriptionProduct.productId,
      },
      { isUpgrade: isUserOnEssential },
    );
  };

  if (!defaultSubscriptionProduct) {
    return null;
  }

  const renderTitleAndDescription = () => (
    <>
      <h2 className={styles.title}>{config.paywall.title}</h2>
      <p className={styles.text} data-testid="description">
        {config.paywall.description}
      </p>
    </>
  );

  const renderCtaButton = (testId: 'sign-up-button-1' | 'sign-up-button-2') => {
    const isMobile = testId === 'sign-up-button-1';
    const className = isMobile ? clsx(styles.button, styles.mobileView) : styles.button;
    const linkClassName = isMobile ? clsx(styles.link, styles.mobileView) : styles.link;

    if (config.paywall.shouldCtaClickOpenModal) {
      return (
        <Button className={className} data-testid={testId} onClick={handleSubscribe} size="large" variant="secondary">
          {config.paywall.ctaText}
        </Button>
      );
    } else {
      const plansHash = repubIs.GZ ? '' : '#plans';
      const href = `${repubPath}/plus?redirectTo=${currentPath}${plansHash}`;

      const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        trackComponentWallCtaClick({
          feature: walledFeature,
          isUpgrade: isUserOnEssential,
          price: defaultSubscriptionProduct?.priceFormatted ?? '',
          selection: config.paywall.ctaClickTrackingProductName,
          term: defaultSubscriptionProduct?.term,
        });
        window.location.href = href;
      };

      return (
        <a className={linkClassName} data-testid={testId} href={href} onClick={handleLinkClick}>
          {userHasCancelledSubscription ? 'Subscribe Now' : config.paywall.ctaText}
        </a>
      );
    }
  };

  const renderLoginText = (customClassName?: string) => {
    if (isLoggedIn) {
      return null;
    }
    return (
      <p className={customClassName ?? styles.text} data-testid="login">
        {config.paywall.loginText}
        <Button className={styles.ghostButton} onClick={handleLoginClick} variant="ghost">
          {t('CX_AUTH_LOGIN')}
        </Button>
      </p>
    );
  };

  if (repub === REPUBS.OC.name) {
    return (
      <div
        className={styles[config.paywall.cssClass]}
        data-testid={config.paywall.testId}
        ref={refForViewableImpressionTracking}
      >
        <div className={styles.content}>
          <PadlockIcon className={styles.padlock} />
          {renderTitleAndDescription()}
          <SubscriptionCard.Container>
            <SubscriptionCard
              featured
              features={config.subscription.premiumFeatures}
              handleSubscribe={handleSubscribe}
              isEssentialSubscriber={isUserOnEssential}
              isLoggedIn={isLoggedIn}
              pricingLabel={config.paywall.pricingLabel}
              product={defaultSubscriptionProduct}
              title={'PREMIUM'}
              userHasCancelledSubscription={userHasCancelledSubscription}
              variant="premium"
            />
          </SubscriptionCard.Container>
        </div>
        {renderLoginText(styles.loginText)}
      </div>
    );
  }

  return (
    <Card
      borderRadius="l"
      {...props}
      className={styles.padlockCard}
      data-template={config.paywall.dataTemplate}
      data-testid="component-wall"
    >
      {renderCtaButton('sign-up-button-1')}
      <PadlockIcon className={styles.padlock} />
      <div className={styles.contentWrapper} ref={refForViewableImpressionTracking}>
        {renderTitleAndDescription()}
        <List
          classNames={{
            wrapper: styles.itemsList,
            iconWrapper: styles.itemIconWrapper,
            checkIcon: styles.itemCheckIcon,
          }}
          data-testid="padlock-list"
          direction="column"
          items={config.subscription.premiumFeatures.map(feature => feature.title)}
          size="s"
        />
      </div>
      <div className={styles.ctaWrapper}>
        {renderCtaButton('sign-up-button-2')}
        {renderLoginText()}
      </div>
    </Card>
  );
}
